import { useState, useRef } from 'react'
import {
  Box,
  H6,
  Text,
  Spinner,
  Grid,
  H3,
  Input,
  InputDescriptionWrap,
} from 'components'
import { gql, useQuery } from '@apollo/client'
import { IDataGrouppedAmenities, COLORS, IGrouppedAmenity } from 'utils'
import styled from '@emotion/styled'
import { rem, ellipsis } from 'polished'
import { useFormikContext } from 'formik'
import { IVenueFormValues } from './FormPage'
import { StyledFlex } from './GeneralInfoStep'
import { TooltipWhenEllipsis } from 'components/TooltipWhenEllipsis'
import { inputDescriptions } from './inputDescriptions'
import { AmenitiesSearch } from './AmenitiesSelect'

const GET_AMENITIES = gql`
  query grouppedAmenities {
    grouppedAmenities {
      amenities {
        id
        name
      }

      id
      name
    }
  }
`

const UnderLineText = styled(Text)`
  &:hover {
    text-decoration: underline;
  }

  margin-top: ${rem(5)};
  text-align: left;
  cursor: pointer;
  color: ${COLORS.SOULSTONE_BLUE};
`

const StyledSpinner = styled(Spinner)`
  height: ${rem(80)};
  width: ${rem(80)};
  align-self: center;
`

const CheckboxWrapper = styled('div')`
  text-align: left;
  ${ellipsis()}
`

const AMENITIES_CONTAINER_HEIGHT = 135
const StyledGrid = styled(Grid)`
  max-height: ${rem(AMENITIES_CONTAINER_HEIGHT)};
  overflow: auto;
  text-transform: capitalize;
`

const AMENITIES_GROUP_COUNT = 6

interface IAmenitiesSelectProps {
  amenities: IGrouppedAmenity['amenities']
}

const AmenitiesSelect = ({ amenities }: IAmenitiesSelectProps) => {
  const { values, setFieldValue } = useFormikContext<IVenueFormValues>()
  const [showMoreFields, setShowMoreFields] = useState<number>(1)
  const containerRef = useRef<HTMLDivElement>(null)

  return (
    <>
      <StyledGrid
        gridTemplateColumns={{ mobile: `1fr`, tablet: `1fr 1fr` }}
        gridGap="s"
        ref={containerRef}
      >
        {amenities
          .slice(0, AMENITIES_GROUP_COUNT * showMoreFields)
          .map(({ id, name }) => {
            const amenityValue = (
              values.additionalInfo.amenities as { [key: string]: boolean }
            )[id]

            return (
              <CheckboxWrapper key={id}>
                <Input.Checkbox
                  key={id}
                  name={`additionalInfo.amenities.${id}`}
                  checked={amenityValue}
                  label={<TooltipWhenEllipsis text={name} />}
                  onChange={() => {
                    setFieldValue(
                      `additionalInfo.amenities.${id}`,
                      amenityValue ? undefined : true
                    )
                  }}
                />
              </CheckboxWrapper>
            )
          })}
      </StyledGrid>

      {amenities.length > AMENITIES_GROUP_COUNT * showMoreFields && (
        <UnderLineText
          as="p"
          onClick={() => {
            setShowMoreFields(showMoreFields + 1)
            if (containerRef?.current) {
              containerRef?.current?.scroll({
                top:
                  containerRef?.current?.scrollTop + AMENITIES_CONTAINER_HEIGHT,
                behavior: 'smooth',
              })
            }
          }}
        >
          Show more...
        </UnderLineText>
      )}
    </>
  )
}

export const MIN_AMENITIES = 12

export const AdditionalInfoStep = () => {
  const { values, setFieldValue } = useFormikContext<IVenueFormValues>()

  const { error, loading, data } =
    useQuery<IDataGrouppedAmenities>(GET_AMENITIES)

  if (error) console.error(error)

  return (
    <>
      {loading ? (
        <StyledSpinner />
      ) : (
        <>
          <H3 fontSize={20}>Tell us about your amenities & facilities.</H3>
          <InputDescriptionWrap
            text={inputDescriptions.additionalInfo.amenities}
          >
            <Text
              as="p"
              fontSize={16}
              fontWeight="500"
              textAlign="left"
              mb={rem(16)}
            >
              Please select at least {MIN_AMENITIES} items and as many as you
              find suitable for your property.
            </Text>

            <AmenitiesSearch />

            {data?.grouppedAmenities?.map(({ name, id, amenities }) => (
              <Box mt={rem(12)} key={id}>
                <H6 textAlign="left" fontWeight={500}>
                  {name}
                </H6>

                <AmenitiesSelect amenities={amenities} />
              </Box>
            ))}
          </InputDescriptionWrap>
        </>
      )}

      <InputDescriptionWrap
        text={inputDescriptions.additionalInfo.customAmenities}
      >
        <Input
          isBlock
          label="Custom amenities"
          placeholder="Enter another amenities"
          name="additionalInfo.customAmenities"
        />
      </InputDescriptionWrap>

      <InputDescriptionWrap
        text={inputDescriptions.additionalInfo.internetSpeed}
      >
        <StyledFlex>
          <Input
            suffix="Mb/s"
            placeholder="Enter speed"
            min={0}
            type="number"
            label="Upload speed"
            name="additionalInfo.upload"
          />

          <Input
            suffix="Mb/s"
            placeholder="Enter speed"
            min={0}
            type="number"
            label="Download speed"
            name="additionalInfo.download"
          />
        </StyledFlex>
      </InputDescriptionWrap>

      <InputDescriptionWrap
        text={inputDescriptions.additionalInfo.carbonEmissionRoom}
      >
        <Input
          isBlock
          suffix="kg / room / night"
          placeholder="Enter room carbon emissions"
          min={0}
          type="number"
          label="Room carbon emissions"
          name="additionalInfo.carbonEmissionsRoom"
        />
      </InputDescriptionWrap>

      <InputDescriptionWrap
        text={inputDescriptions.additionalInfo.hasMeetingRooms}
      >
        <Input.Checkbox
          checked={values.additionalInfo.hasMeetingRooms as boolean}
          label="I have a meeting room or a communal space"
          name="additionalInfo.hasMeetingRooms"
          onChange={(val) =>
            setFieldValue('additionalInfo.hasMeetingRooms', val)
          }
        />
      </InputDescriptionWrap>

      {values.additionalInfo.hasMeetingRooms && (
        <InputDescriptionWrap
          text={inputDescriptions.additionalInfo.carbonEmissionMeeting}
        >
          <Input
            isBlock
            suffix="kg / mm2 / hour"
            placeholder="Enter meeting room carbon emissions"
            min={0}
            type="number"
            label="Meeting room carbon emissions"
            name="additionalInfo.carbonEmissionsMeeting"
          />
        </InputDescriptionWrap>
      )}
    </>
  )
}
